/**
 * discGlyphComposite.ts — the EXACT-ACT disc compositor (icon rebuild Phase 2a).
 *
 * Founder re-ruling 2026-07-05 (docs/icon-urgency-council-2026-07-05.md §C.1, BINDING):
 * in-app hazard markers must look EXACTLY like the ACT column of the EXACT contact sheet
 * (docs/design-refs/cahs/cowork-contact-sheet-EXACT.png) — a solid coloured DISC with the
 * white EXACT glyph knocked out at centre. The disc FILL is the Response Level (the P1
 * panel palette) when unworked, or ACK-STEEL (the sheet's ACKED column) when worked. This
 * RETIRES the white-plate act CHIP (iconRegistry.actChipCompositeSvg / renderCahsActChip)
 * for the hazard hotspot family — it is a rip-up, not a restyle. (The chip SURVIVES for the
 * flood/ice/smoke/CAP/zone families until Phase 2b.)
 *
 * THE LOAD-BEARING RENDER FACT (§C.1 build spec): the EXACT glyphs are solid single
 * `currentColor` paths whose baked category frame + interior detail are winding-rule
 * ("alpha-cutout") holes. To read as a KNOCKOUT on a coloured disc the glyph must be
 * re-inked to a WHITE LITERAL and composited OVER the disc — tinting it to the disc's own
 * colour would VANISH it (same-colour on same-colour). So the composite is: a `currentColor`
 * circle DISC (retinted to the fill downstream) with the glyph re-inked to a fixed white
 * literal on top. The white ink is what makes the knockout read.
 *
 * Canvas-free (pure string work through svgIcon.tintedSvgDataUrl), so it renders identically
 * in unit tests + the headless preview, and caches one image per (glyph × fill × state). The
 * SAME compositor backs the runtime marker AND the contact-sheet generator (§C.1-4 realization
 * path) — cahsContactSheet.parity.test.ts byte-mirrors them so they can never silently drift.
 */
import { CAHS_GLYPHS, type CahsGlyphKey } from './assets/cahs/index.generated';
import { tintedSvgDataUrl } from './svgIcon';

/**
 * The white knockout ink — a FIXED literal (never retinted). The EXACT glyph is re-inked to
 * this so the downstream `currentColor`→fill retint leaves it white; it reads as a knockout
 * over the coloured disc (the load-bearing render fact above). Deliberately pure white so the
 * knockout clears its legibility floor on every level fill + steel at 14px (asserted in
 * styles/colourLaw.deuteranopia.test.ts).
 */
export const DISC_GLYPH_INK = '#ffffff';

/**
 * The EXACT glyph's linear scale INSIDE the disc (icon rebuild Phase 2a).
 *
 * Founder note (2026-07-05, §C.1): "the size of the symbols inside likely needs to be
 * increased." Derivation: every EXACT glyph is authored so its baked category-frame diamond
 * fills viewBox 8..92 (vertices 42 units from the 50,50 centre) — i.e. at scale 1 the diamond
 * points sit at 42/50 = 0.84 of the disc radius. Bumping the glyph ~15% larger lands the
 * points at 42·1.15/50 ≈ 0.97 of the radius, so the disc colour only "bites" the four corner
 * arcs between the diamond's flat sides — the founder-approved EXACT-sheet look. Held under
 * the 50/42 ≈ 1.19 clip ceiling (past which the points poke out of the disc into the dark
 * globe). This is a ONE-LINE TUNE: the founder eyeball finalizes the number; the 14px
 * white-on-fill legibility floor is pinned in the colour-law gate regardless of the value.
 */
export const DISC_GLYPH_SCALE = 1.15;

/** Inner markup of a single-`<svg>` EXACT glyph — its positioning `<g transform>` + `<path>`. */
function glyphInner(rawSvg: string): string {
  const m = rawSvg.match(/<svg[^>]*>([\s\S]*)<\/svg>/);
  return m ? m[1]! : '';
}

/**
 * OWNERSHIP RING (feat/ownership-coordination) — an ADDITIVE, viewer-relative keyline baked INTO
 * the disc canvas so it scales WITH the disc by construction (the prior separate-stroke outline
 * desynced on zoom — it scaled independently). It NEVER touches the disc fill/size/opacity: an
 * owned hazard is STILL BURNING. The stroke is a FIXED LITERAL colour (like the white glyph ink) so
 * it survives the downstream currentColor→fill retint and never vanishes.
 *
 *   • ring only          → OWNED BY ANOTHER AGENCY (the steel keyline).
 *   • ring + inner mark  → OWNED BY ME (the same keyline + a distinguishing inner keyline).
 *   • no ring            → UN-OWNED (the ABSENCE is the orphan alarm).
 *
 * Geometry (viewBox 0..100, disc r=50): the outer keyline rides the disc rim; the "mine" inner
 * keyline sits just inside it with a small gap. Widths are in viewBox units so they auto-scale.
 */
export type DiscOwnershipRing = { color: string; width?: number; inner?: string };
const RING_OUTER_W = 8;
const RING_INNER_W = 4;
const RING_INNER_GAP = 1.5;

/** Bake the ownership keyline(s) into the composite SVG, or '' when unowned (byte-identical to the
 *  ringless composite so the parity + currentColor-count locks hold). */
function ringMarkup(ring: DiscOwnershipRing | undefined): string {
  if (!ring) return '';
  const outerW = ring.width ?? RING_OUTER_W;
  const outerR = 50 - outerW / 2; // rides the rim, fully inside the viewBox
  let out = `<circle cx="50" cy="50" r="${outerR}" fill="none" stroke="${ring.color}" stroke-width="${outerW}"/>`;
  if (ring.inner) {
    const innerR = 50 - outerW - RING_INNER_GAP - RING_INNER_W / 2;
    out += `<circle cx="50" cy="50" r="${innerR}" fill="none" stroke="${ring.inner}" stroke-width="${RING_INNER_W}"/>`;
  }
  return out;
}

/** Cache of the UNTINTED disc composite SVG string, keyed by `${glyph}|${state}|${ring}`. */
const discCompositeCache = new Map<string, string | null>();

/**
 * Build the UNTINTED disc-glyph composite (icon rebuild Phase 2a): a plain `currentColor`
 * circle DISC (retinted to the level/steel fill downstream) with the EXACT glyph re-inked to
 * the white knockout literal, scaled about centre by {@link DISC_GLYPH_SCALE}, composited on
 * top. `glyphless` (the 'detected' incident-lifecycle state) omits the glyph → a bare disc
 * (the earliest lifecycle read: an unconfirmed hotspot commits to no hazard kind). Returns
 * null if the glyph key is unknown.
 *
 * Exported (the `…Svg` = untinted-string convention, mirroring iconRegistry.actChipCompositeSvg)
 * so the contact-sheet generator bakes the TRUE composite for byte-equality parity, not a mock.
 */
export function discGlyphCompositeSvg(
  glyph: CahsGlyphKey,
  opts: { glyphless?: boolean; ring?: DiscOwnershipRing } = {},
): string | null {
  // The ring is an ADDITIVE variant — fold it into the cache key so a same-glyph/same-state
  // disc with a different (or no) ownership ring never returns a stale cached image. When no
  // ring is set the key + the emitted SVG stay byte-identical to before (parity locks hold).
  const ringKey = opts.ring
    ? `|ring:${opts.ring.color}:${opts.ring.width ?? ''}:${opts.ring.inner ?? ''}`
    : '';
  const cacheKey = `${glyph}|${opts.glyphless ? 'plate' : 'glyph'}${ringKey}`;
  const cached = discCompositeCache.get(cacheKey);
  if (cached !== undefined) return cached;
  const glyphRaw = (CAHS_GLYPHS as Record<string, string>)[glyph];
  const inner = glyphRaw ? glyphInner(glyphRaw) : '';
  if (!glyphRaw || !inner) {
    discCompositeCache.set(cacheKey, null);
    return null;
  }
  // The glyph re-inked to the WHITE knockout literal, so the currentColor→fill retint
  // downstream tints ONLY the disc and leaves the glyph white (the knockout).
  const whiteGlyph = inner.replaceAll('currentColor', DISC_GLYPH_INK);
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 100 100">` +
    // the DISC — a plain circle filled with currentColor (retinted to the level/steel fill).
    `<circle cx="50" cy="50" r="50" fill="currentColor"/>` +
    // the EXACT glyph, white, scaled about centre; 'detected' (glyphless) omits it → bare disc.
    (opts.glyphless
      ? ''
      : `<g transform="translate(50 50) scale(${DISC_GLYPH_SCALE}) translate(-50 -50)">${whiteGlyph}</g>`) +
    // the OWNERSHIP keyline(s), literal-stroked, ON TOP so they always read; '' when unowned.
    ringMarkup(opts.ring) +
    `</svg>`;
  discCompositeCache.set(cacheKey, svg);
  return svg;
}

/**
 * Render the disc-glyph composite as a tinted `image/svg+xml` data-URL — the EXACT-ACT
 * marker image a Cesium billboard consumes. `fill` is the DISC colour: RESPONSE_LEVEL_COLORS
 * [level] for an unworked marker, or ACK-STEEL when worked/acked (the EXACT sheet's ACKED
 * column — the steel disc). The white glyph is a fixed literal, untouched by the
 * currentColor→fill retint. Canvas-free + cached by (glyph, state, fill), so it renders
 * identically in unit tests. Returns null if the glyph key is unknown (caller falls back to
 * a plain disc / no marker). Mirrors iconRegistry.renderCahsActChip.
 */
export function renderDiscGlyph(
  glyph: CahsGlyphKey,
  opts: { fill: string; glyphless?: boolean; ring?: DiscOwnershipRing },
): string | null {
  const composite = discGlyphCompositeSvg(glyph, { glyphless: opts.glyphless, ring: opts.ring });
  if (composite === null) return null;
  // Fold the ring variant into the tint cache key too (svgIcon caches by `${key}|${color}`) — else
  // a same-fill/different-ring disc returns the wrong cached image. Byte-identical when no ring.
  const ringKey = opts.ring
    ? `:ring:${opts.ring.color}:${opts.ring.width ?? ''}:${opts.ring.inner ?? ''}`
    : '';
  return tintedSvgDataUrl(
    `disc:${glyph}${opts.glyphless ? ':plate' : ''}${ringKey}`,
    composite,
    opts.fill,
  );
}
